import React, { useEffect, useState, useRef } from 'react';
import { subscribeConfirms } from '../../utils/toast';

const variantAccent = {
  info: 'text-sky-600 bg-sky-50 border-sky-200',
  success: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  warning: 'text-amber-600 bg-amber-50 border-amber-200',
  error: 'text-rose-600 bg-rose-50 border-rose-200',
};

const ConfirmHost = () => {
  const [queue, setQueue] = useState([]);
  const stylesInjected = useRef(false);

  useEffect(() => {
    if (!stylesInjected.current) {
      const styleId = 'tp-confirm-keyframes';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          @keyframes tp-modal-in {
            from { transform: translateY(12px) scale(0.98); opacity: 0; }
            to { transform: translateY(0) scale(1); opacity: 1; }
          }
        `;
        document.head.appendChild(style);
      }
      stylesInjected.current = true;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeConfirms((request) => {
      setQueue((prev) => [...prev, request]);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const current = queue[0];

  useEffect(() => {
    if (!current) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        resolveCurrent(false);
      }
      if (event.key === 'Enter') {
        resolveCurrent(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [current]);

  const resolveCurrent = (result) => {
    if (!current) return;
    try {
      current.resolve(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Confirm resolve failed', error);
    }
    setQueue((prev) => prev.slice(1));
  };

  if (!current) return null;

  const accentClass = variantAccent[current.variant] || variantAccent.info;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-100 overflow-hidden"
        style={{ animation: 'tp-modal-in 160ms ease-out' }}
      >
        <div className={`px-5 py-4 border-b text-sm font-semibold uppercase tracking-wide ${accentClass}`}>
          {current.title || 'Please confirm'}
        </div>
        <div className="px-5 py-5 text-slate-700 text-base leading-relaxed">
          {current.message || 'Are you sure you want to continue?'}
        </div>
        <div className="px-5 pb-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-200"
            onClick={() => resolveCurrent(false)}
          >
            {current.cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              current.destructive
                ? 'bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-500'
                : 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500'
            }`}
            onClick={() => resolveCurrent(true)}
          >
            {current.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmHost;


